import React, { useCallback, useEffect, useMemo, useContext } from 'react';
import { FormattedMessage } from 'react-intl-next';
import type { LoomVideo } from '@loomhq/record-sdk';

import VideoIcon from '@atlaskit/icon/core/video';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Box, Text, Flex, xcss, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge';
import Button, { LinkButton } from '@atlaskit/button/new';

import { useLoomAdControlState } from '@confluence/loom-utils/entry-points/useLoomAdControlState';
import { LoomAdControl } from '@confluence/loom-utils/entry-points/LoomAdControl';
import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { useLoomCrossJoinUrl } from '@confluence/loom-utils/entry-points/useLoomCrossJoinUrl';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint';
import { useLoomInsertUrlActions } from '@confluence/loom-insert-url-store';
import { type FlagsStateContainer } from '@confluence/flags';
import { getUrlForContentType } from '@confluence/content-types-utils';
import { useSpaceKey } from '@confluence/space-utils';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';

import { useLoomScriptCrossFlowContext } from '../context/LoomScriptCrossFlowContext';
import { messages } from '../messages';
import { useRovoLoomScriptEntryPoint } from '../hooks/useRovoLoomScriptEntryPoint';

import { ViewScriptButton } from './ViewScriptButton';
import { LoomScriptExtensionSpotlightWrapper } from './LoomScriptExtensionSpotlightWrapper';

export const CONTENT_TYPE = 'page';
export const ENTRY_POINT_ID = 'loom-confluence-read-time-dropdown-loom-script-extension';
export const SOURCE_COMPONENT = 'readTimeDropdownLoomRecording';

const sectionLabelStyles = xcss({
	marginBottom: 'space.100',
	position: 'relative',
});

const loomVideoExtensionWrapperStyles = xcss({
	width: '432px',
	paddingBlock: 'space.100',
	paddingInline: 'space.250',
	marginInline: 'space.negative.250',
	marginBottom: 'space.negative.100',
	background: token('elevation.surface.raised'), // should be elevation.surface.overlay, but ReadTimePopup.tsx has this
});

const sectionLabelTextStyles = xcss({
	backgroundColor: 'elevation.surface.raised',
	zIndex: '1',
	paddingRight: 'space.100',
});

const horizontalDividerStyles = xcss({
	position: 'absolute',
	borderBottom: `1px solid ${token('color.border')}`,
	height: '1px',
	width: '100%',
});

export type LoadEditor = ({ args }: { [key: string]: string }) => void;

export type UseLoomScriptRecordingProps = {
	contentId: string;
	flags: FlagsStateContainer;
	/**
	 * This is ONLY for analytics purposes, it will not affect the rendering of the component
	 */
	readTimeSeconds?: number;
	onCloseAction?: () => void; // when a user clicks one of the action buttons, we use this to close the popup
};

export const ReadTimeDropdownLoomScriptExtension = ({
	contentId,
	flags,
	readTimeSeconds,
	onCloseAction,
}: UseLoomScriptRecordingProps) => {
	const { loadEditor } = useEditPageLoadingActions();
	const { error, entryPointVariant } = useLoomEntryPointVariant();
	const loomInsertUrlActions = useLoomInsertUrlActions();
	const spaceKey = useSpaceKey() || '';
	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });
	const crossJoinUrl = useLoomCrossJoinUrl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isSiteAdmin } = useContext(SPAViewContext);

	const { openLoomCrossFlow } = useLoomScriptCrossFlowContext();

	const redirectUrl =
		getUrlForContentType({
			contentType: CONTENT_TYPE,
			spaceKey,
			contentId,
			redirectToEditor: true,
		}) || '';

	const saveLoomUrlAndLoadEditor = useCallback(
		(video: LoomVideo) => {
			loomInsertUrlActions.updateFullPageEditorLoomUrl(video);

			loadEditor &&
				loadEditor({
					contentId,
					spaceKey,
					redirectUrl,
					contentType: CONTENT_TYPE,
				});
		},
		[loomInsertUrlActions, contentId, spaceKey, loadEditor, redirectUrl],
	);

	const { isInterestedInLoom, notInterestedInLoomCallback } = useLoomAdControlState({
		touchpoint: ENTRY_POINT_ID,
	});

	const handleNotInterested = useCallback(async () => {
		await notInterestedInLoomCallback();
	}, [notInterestedInLoomCallback]);

	const openLoomRecorderParams = useMemo(
		() => ({
			onInsert: (_: unknown, video: LoomVideo) => {
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		}),
		[saveLoomUrlAndLoadEditor],
	);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: SOURCE_COMPONENT,
		flags,
		resetInsertFunctionOnUnmount: false,
	});

	const onRecordVideoClick = useCallback(() => {
		void openLoomRecorder(openLoomRecorderParams);
	}, [openLoomRecorder, openLoomRecorderParams]);

	const { openRovoLoomScript } = useRovoLoomScriptEntryPoint(entryPointVariant);
	const viewScriptButtonOnClick = useCallback(() => {
		openRovoLoomScript?.();
		onCloseAction?.();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'loomScriptReadTimeDropDownExtensionSeeAiScript',
				eventType: 'ui',
				source: SOURCE_COMPONENT,
				attributes: {
					entryPointVariant,
					readTimeSeconds,
					isSiteAdmin,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		openRovoLoomScript,
		entryPointVariant,
		readTimeSeconds,
		isSiteAdmin,
		onCloseAction,
	]);

	/**
	 * This is seperate to {@link crossFlowRecordButtonClickedAnalyticsEventHandler} because in
	 * the UI these are two different buttons with distinct labels
	 */
	const coUseOrCrossJoinRecordButtonClickedAnalyticsEventHandler = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'loomScriptReadTimeDropDownExtensionRecord',
				eventType: 'ui',
				source: SOURCE_COMPONENT,
				attributes: {
					entryPointVariant,
					readTimeSeconds,
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, entryPointVariant, readTimeSeconds, isSiteAdmin]);

	const crossFlowRecordButtonClickedAnalyticsEventHandler = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'loomScriptReadTimeDropDownExtensionRecordWithAiScript',
				eventType: 'ui',
				source: SOURCE_COMPONENT,
				attributes: {
					entryPointVariant,
					readTimeSeconds,
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, entryPointVariant, readTimeSeconds, isSiteAdmin]);

	/**
	 * If the component meets the condition to not return `null`, fire the rendered event
	 */
	useEffect(() => {
		if (isInterestedInLoom)
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: SOURCE_COMPONENT,
					action: 'rendered',
					actionSubjectId: SOURCE_COMPONENT,
					source: SOURCE_COMPONENT,
					attributes: {
						readTimeSeconds,
						entryPointVariant,
						isSiteAdmin,
					},
				},
			}).fire();
	}, [isInterestedInLoom, createAnalyticsEvent, entryPointVariant, readTimeSeconds, isSiteAdmin]);

	const handleRecordClick = useCallback(() => {
		onCloseAction?.();
		if (error) {
			loomEntryPointErrorCallback();
			return;
		}
		if (entryPointVariant === 'CROSS_FLOW') {
			crossFlowRecordButtonClickedAnalyticsEventHandler();
			void openLoomCrossFlow();
		}
		if (entryPointVariant === 'CO_USE') {
			coUseOrCrossJoinRecordButtonClickedAnalyticsEventHandler();
			onRecordVideoClick();
		}
	}, [
		crossFlowRecordButtonClickedAnalyticsEventHandler,
		onCloseAction,
		error,
		entryPointVariant,
		loomEntryPointErrorCallback,
		openLoomCrossFlow,
		onRecordVideoClick,
		coUseOrCrossJoinRecordButtonClickedAnalyticsEventHandler,
	]);

	const handleCoUseOrCrossJoinRecordButtonClick = useCallback(() => {
		onCloseAction?.();
		coUseOrCrossJoinRecordButtonClickedAnalyticsEventHandler();
	}, [coUseOrCrossJoinRecordButtonClickedAnalyticsEventHandler, onCloseAction]);

	if (!isInterestedInLoom) {
		return null;
	}

	return (
		<>
			<Flex key="loomAIScriptSectionLabel" xcss={sectionLabelStyles} alignItems="center">
				<Inline xcss={sectionLabelTextStyles}>
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage {...messages.readTimeImprovementLabel} />
					</Text>
				</Inline>
				<Box xcss={horizontalDividerStyles} />
			</Flex>

			<LoomScriptExtensionSpotlightWrapper readTimeSeconds={readTimeSeconds}>
				<Box key="videoSummary" xcss={loomVideoExtensionWrapperStyles}>
					<Flex justifyContent="space-between" alignItems="center">
						<Flex alignItems="center" gap="space.100">
							<VideoIcon label="Loom" />
							<FormattedMessage {...messages.readTimeLoomAIScriptLabel} />
							{(entryPointVariant === 'CROSS_FLOW' || entryPointVariant === 'CROSS_JOIN') && (
								<Lozenge appearance="new">
									<FormattedMessage {...messages.tryLozenge} />
								</Lozenge>
							)}
						</Flex>
						<Flex alignItems="center" gap="space.100">
							{entryPointVariant === 'CROSS_FLOW' && (
								<>
									<Button onClick={handleRecordClick}>
										<FormattedMessage {...messages.recordCrossFlowButton} />
									</Button>
									<LoomAdControl
										entryPointId={ENTRY_POINT_ID}
										onDismiss={handleNotInterested}
										spacing="default"
									/>
								</>
							)}

							<>
								{(entryPointVariant === 'CROSS_JOIN' || entryPointVariant === 'CO_USE') && (
									<ViewScriptButton onClick={viewScriptButtonOnClick} />
								)}
								{entryPointVariant === 'CO_USE' && (
									<Button onClick={handleRecordClick}>
										<FormattedMessage {...messages.recordButton} />
									</Button>
								)}
								{entryPointVariant === 'CROSS_JOIN' && (
									<LinkButton
										onClick={handleCoUseOrCrossJoinRecordButtonClick}
										href={crossJoinUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										<FormattedMessage {...messages.recordButton} />
									</LinkButton>
								)}
							</>
						</Flex>
					</Flex>
				</Box>
			</LoomScriptExtensionSpotlightWrapper>
		</>
	);
};
